// Popup Cascade — one state machine driving three chances to capture an email:
//
//   stage 1  Alia popup, opened by Alia's own triggers
//   stage 2  Alia popup with no triggers of its own, opened by us via the SDK
//   stage 3  the worm teaser (our own DOM) which, on click, opens a third Alia
//            campaign — the worm has no form of its own
//
// Alia performs every capture and writes to Klaviyo through its own
// integration. We only decide who gets which stage and when, and log the
// winning stage for attribution. Campaign ids and all timing come from the
// HeapsGood Ops config, so tuning the cascade never needs a theme deploy.
//
// State machine:
//   inert     ─(enabled + eligible + configured)─▶ observing
//   observing ─(stage-1 popupView)───────────────▶ popup_open
//   popup_open ─(popupClose, no signup)──────────▶ gap
//   gap       ─(full stage-2 delay + gates met)────▶ opening ─(popupView)─▶ popup_open
//   gap       ─(grace elapsed + gates met)───────▶ peeking
//   peeking   ─(worm clicked)────────────────────▶ open worm campaign
//   peeking   ─(× clicked)───────────────────────▶ dismissed
//   any       ─(popupView while worm showing)────▶ retract  (newest popup wins)
//   any       ─(alia:signup)─────────────────────▶ converted
//
// Two Alia behaviours shape the defensive bits below:
//   1. `alia:popupClose` fires on close OR minimise, and the ordering of
//      `alia:signup` against it is not documented. So a close never acts
//      immediately — it arms a timer, and the timer re-checks the converted
//      flag at the moment of truth. Every delay has a floor above zero, which
//      is what makes that re-check reliable.
//   2. Alia has no "is this visitor already a subscriber" API. Suppression is
//      therefore Alia's own campaign targeting (we open stages 2 and 3 with
//      `ignoreTargeting: false`, so its rules still apply) plus our own
//      365-day cookie, set the moment we see a signup anywhere.
//   3. Alia announces every campaign it may show on a page (`alia:campaignMatched`)
//      the moment its SDK initialises, a few seconds BEFORE the campaign's own
//      trigger shows it — and it queues a popup that triggers while another is
//      open, showing it the instant that one closes. An SDK open pushed inside
//      that window therefore produces two popups back to back. So a follow-up
//      never fires while Alia has announced a popup it hasn't shown yet, or
//      before its SDK has had a chance to announce anything. See ALIA_TURN_MS.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Cookie / sessionStorage keys. The `hg-worm-*` names are deliberately
// unchanged: visitors carrying a cooldown from the previous implementation
// should keep it rather than get a fresh popup because we renamed a key.
const CK = {
    dismissed: 'hg-worm-dismissed',
    closed: 'hg-worm-closed',
    subscribed: 'hg-worm-subscribed',
    visits: 'hg-worm-visits',
};
const SS = {
    sessionId: 'hg-worm-session-id',
    t0: 'hg-worm-t0',
    pages: 'hg-worm-pages',
    closedAt: 'hg-casc-closed-at-v2',
    primaryClosedAt: 'hg-casc-primary-closed-at-v2',
    sessionCounted: 'hg-worm-session-counted',
    done: 'hg-worm-done',
    converted: 'hg-casc-converted',
    config: 'hg-casc-config',
    stage2: 'hg-casc-stage2-attempted',
};
const CONFIG_TTL_MS = 30 * 60 * 1000;
// Minimum settle before the worm may peek on ANY page load. Session-scoped
// timers can resolve to "fire now" mid-session (e.g. arriving on page 3 after
// 60s), which would put the worm on screen at first paint and read as "it just
// appeared". This floor guarantees it always animates in a beat AFTER the page
// settles. It doubles as the window in which a late signup event can still
// cancel a stage — see the header note.
const MIN_SETTLE_MS = 2500;
// A configured delay below ten seconds still gets a minimum quiet period.
// Neither exit intent nor the return-visitor boost can shorten this delay.
const MIN_GAP_AFTER_CLOSE_MS = 10000;
// How long Alia gets to show a popup it has announced, and how long we wait for
// its SDK to initialise on a fresh page before a follow-up may fire without
// hearing from it. Alia's basic triggers on this store are 5s and 15s on page,
// so 20s covers them with margin. It cannot be open-ended: Alia announces a
// campaign even to a visitor its frequency cap will suppress (verified live
// 12 Oct 2026 — a visitor who had just closed stage 1 got `campaignMatched` on
// the next page and no popup), so waiting for the popup would starve stage 2
// for exactly the returning visitors it exists for.
const ALIA_TURN_MS = 20000;
class PopupCascade extends HTMLElement {
    constructor() {
        super(...arguments);
        this.state = 'inert';
        // From data-* attributes
        this.configUrlBase = '';
        this.market = '';
        // Timing — Standard preset; the Ops config overrides each field it supplies.
        this.graceSeconds = 25;
        this.minPages = 2;
        this.minSeconds = 45;
        this.returnVisitBoost = true;
        this.exitIntentEnabled = false;
        this.dismissDays = 5;
        this.closeDays = 10;
        this.subscribedDays = 365;
        this.suppressWhenCartNonempty = false;
        // Cascade wiring — nothing fires until the config supplies real campaign ids.
        this.stage1Ids = [];
        this.stage2Id = null;
        this.stage2DelaySeconds = 30;
        this.wormId = null;
        this.wormDesktopOnly = true;
        // Runtime
        //
        // openStage is the only trustworthy record of which stage is on screen. Stage 2
        // and the worm may share one campaign id, so an event's campaignID cannot always
        // be resolved back to a stage — see stageOf() and handlePopupClose().
        this.openStage = null;
        this.openCampaignId = null;
        this.openPopupId = null;
        this.revision = 0;
        this.sessionMemory = new Map();
        this.graceTimer = null;
        this.engagementTimer = null;
        this.stage2Timer = null;
        // Alia listeners are bound before the config fetch resolves, so events can
        // arrive while stage1Ids/stage2Id/wormId are still empty and stageOf() would
        // classify everything as null. Those events queue here and replay once the
        // campaign ids are known.
        this.ready = false;
        this.pending = [];
        // What we last asked Alia to open, consumed by the popupView that follows.
        // The only way to tell stage 2 from the worm when they share a campaign id.
        this.pendingOpen = null;
        // Campaigns Alia has announced on this page and not yet shown, by campaign
        // id → when. Entries clear on the matching popupView; stale ones (a capped
        // visitor) simply age out of the ALIA_TURN_MS window.
        this.announced = new Map();
        this.bootAt = Date.now();
        this.onPopupView = (e) => this.handlePopupView(e);
        this.onPopupClose = (e) => this.handlePopupClose(e);
        this.onSignup = (e) => this.handleSignup(e);
        this.onCampaignMatched = (e) => this.handleCampaignMatched(e);
        this.onExitIntent = (e) => this.handleExitIntent(e);
        this.onPageHide = () => this.handlePageHide();
        this.onPageShow = (e) => {
            if (e.persisted && this.ready && this.state !== 'popup_open' && this.state !== 'opening' && this.eligible()) {
                this.resumeTiming();
            }
        };
    }
    connectedCallback() {
        if (this.dataset.enabled !== 'true')
            return;
        this.configUrlBase = (this.dataset.configUrl || '').trim();
        this.market = (this.dataset.market || '').trim();
        const peeker = this.querySelector('[data-peeker]');
        if (!peeker)
            return;
        this.peeker = peeker;
        this.dismissBtn = this.querySelector('[data-dismiss]');
        // Any storage/cookie/network failure (private mode, blocked, offline) →
        // the cascade goes inert rather than throwing.
        void this.init().catch(() => {
            this.clearTimers();
            this.state = 'inert';
        });
    }
    disconnectedCallback() {
        this.revision++;
        this.clearTimers();
        this.unbind();
        document.removeEventListener('mouseout', this.onExitIntent);
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.ensureSessionId();
            this.trackSession();
            // Local gates first — cheap, and they don't need the network.
            if (this.ckGet(CK.subscribed) ||
                this.ckGet(CK.dismissed) ||
                this.ckGet(CK.closed) ||
                this.ssGet(SS.converted) === '1') {
                this.state = 'suppressed';
                return;
            }
            // Bind before the network call, not after. Alia opens the primary popup on
            // its own triggers and does not wait for us, so a stage-1 view that lands
            // inside the config fetch was being dropped entirely: SS.popupSeen stayed
            // unset and the fallback path then concluded stage 1 never appeared and
            // opened stage 2 over the top of a popup already on screen. Events arriving
            // before the campaign ids are known are queued rather than classified,
            // because stageOf() would return null for all of them.
            this.bindAliaEvents();
            // `active` folds together the Ops kill switch and the market allow-list.
            // Fail closed on any error.
            const cfg = yield this.loadRemoteConfig();
            if (!this.isConnected)
                return;
            if (!cfg.active) {
                this.standDown();
                return;
            }
            this.applyTiming(cfg.timing);
            this.applyCascade(cfg.cascade);
            // With no stage-1 campaigns we can't tell the primary popup from any other
            // Alia campaign on the site, so there's no anchor to run a cascade off.
            if (this.stage1Ids.length === 0) {
                this.standDown();
                return;
            }
            this.bindUi();
            // Exit intent now serves stage 2 as well as the worm, so it must bind when
            // EITHER has something to open — gating it on wormAllowed() alone meant a
            // cascade with no worm campaign (the current setup) never armed it at all.
            // Desktop-only regardless: `mouseout` toward the tab bar has no mobile
            // equivalent, and touch devices fire it spuriously on scroll.
            if (this.exitIntentEnabled && this.isDesktop() && (this.stage2Id !== null || this.wormAllowed())) {
                document.addEventListener('mouseout', this.onExitIntent);
            }
            this.resumeTiming();
            // Drain last: a popup that is already on screen has to win over whatever
            // resumeTiming() just armed, and handlePopupView() clears those timers.
            this.ready = true;
            this.drainPending();
        });
    }
    /** Stand down without leaving listeners or a queue of unclassifiable events behind. */
    standDown() {
        this.pending.length = 0;
        this.unbind();
        this.state = 'suppressed';
    }
    drainPending() {
        const queued = this.pending.splice(0, this.pending.length);
        for (const ev of queued) {
            if (ev.kind === 'view')
                this.handlePopupView(new CustomEvent('replay', { detail: ev.detail }));
            else if (ev.kind === 'close')
                this.handlePopupClose(new CustomEvent('replay', { detail: ev.detail }));
            else if (ev.kind === 'matched')
                this.handleCampaignMatched(new CustomEvent('replay', { detail: ev.detail }));
            else
                this.handleSignup(new CustomEvent('replay', { detail: ev.detail }));
        }
    }
    // ---- config ----
    configUrl() {
        const q = this.market ? `?market=${encodeURIComponent(this.market)}` : '';
        return this.configUrlBase + q;
    }
    captureUrl() {
        return this.configUrlBase.replace(/\/config\/?$/, '/capture');
    }
    /**
     * Fetch the Ops config, cached in sessionStorage for 30 minutes. Any non-2xx
     * or network failure → { active: false }, so a backend problem leaves the
     * storefront exactly as it was before this feature existed.
     */
    loadRemoteConfig() {
        return __awaiter(this, void 0, void 0, function* () {
            const cachedRaw = this.ssGet(SS.config);
            if (cachedRaw) {
                try {
                    const c = JSON.parse(cachedRaw);
                    if (c && typeof c.ts === 'number' && Date.now() - c.ts < CONFIG_TTL_MS) {
                        return {
                            active: !!c.active,
                            timing: obj(c.timing),
                            cascade: obj(c.cascade),
                        };
                    }
                }
                catch (_a) {
                    /* corrupt cache — fall through and re-fetch */
                }
            }
            try {
                const res = yield fetch(this.configUrl(), { headers: { Accept: 'application/json' } });
                if (!res.ok)
                    return { active: false, timing: null, cascade: null };
                const data = yield res.json();
                const parsed = {
                    active: !!data.active,
                    timing: obj(data.timing),
                    cascade: obj(data.cascade),
                };
                this.ssSet(SS.config, JSON.stringify(Object.assign(Object.assign({}, parsed), { ts: Date.now() })));
                return parsed;
            }
            catch (_b) {
                return { active: false, timing: null, cascade: null };
            }
        });
    }
    /**
     * Per-field precedence: a config value wins only when it's the right type,
     * so a partial or garbled payload can never blank out a working default.
     */
    applyTiming(t) {
        if (!t)
            return;
        if (Number.isFinite(t.graceSeconds))
            this.graceSeconds = t.graceSeconds;
        if (Number.isFinite(t.minPages))
            this.minPages = t.minPages;
        if (Number.isFinite(t.minSeconds))
            this.minSeconds = t.minSeconds;
        if (typeof t.returnVisitBoost === 'boolean')
            this.returnVisitBoost = t.returnVisitBoost;
        if (typeof t.exitIntent === 'boolean')
            this.exitIntentEnabled = t.exitIntent;
        if (Number.isFinite(t.dismissDays))
            this.dismissDays = t.dismissDays;
        if (Number.isFinite(t.closeDays))
            this.closeDays = t.closeDays;
        if (typeof t.suppressCartNonempty === 'boolean')
            this.suppressWhenCartNonempty = t.suppressCartNonempty;
    }
    applyCascade(c) {
        if (!c)
            return;
        if (Array.isArray(c.stage1CampaignIds)) {
            this.stage1Ids = c.stage1CampaignIds.map(campaignId).filter((n) => n !== null);
        }
        const s2 = obj(c.stage2);
        if (s2) {
            this.stage2Id = campaignId(s2.campaignId);
            if (Number.isFinite(s2.delaySeconds))
                this.stage2DelaySeconds = s2.delaySeconds;
        }
        if (this.stage2Id !== null && this.stage1Ids.includes(this.stage2Id))
            this.stage2Id = null;
        const w = obj(c.worm);
        if (w) {
            this.wormId = campaignId(w.campaignId);
            if (typeof w.desktopOnly === 'boolean')
                this.wormDesktopOnly = w.desktopOnly;
            if (this.wormId !== null && this.stage1Ids.includes(this.wormId))
                this.wormId = null;
        }
    }
    // ---- events ----
    /** Alia's own events. Bound before the config fetch — see init(). */
    bindAliaEvents() {
        document.addEventListener('alia:popupView', this.onPopupView);
        document.addEventListener('alia:popupClose', this.onPopupClose);
        document.addEventListener('alia:signup', this.onSignup);
        document.addEventListener('alia:campaignMatched', this.onCampaignMatched);
        // pagehide, not beforeunload: it fires for bfcache navigations too, and it
        // is the only signal we get when someone leaves with a popup still open.
        window.addEventListener('pagehide', this.onPageHide);
        window.addEventListener('pageshow', this.onPageShow);
    }
    /** Our own controls. Only worth binding once the cascade is actually going to run. */
    bindUi() {
        var _a;
        this.peeker.addEventListener('click', () => this.openWormCampaign());
        (_a = this.dismissBtn) === null || _a === void 0 ? void 0 : _a.addEventListener('click', (e) => {
            e.stopPropagation();
            this.dismiss();
        });
    }
    unbind() {
        document.removeEventListener('alia:popupView', this.onPopupView);
        document.removeEventListener('alia:popupClose', this.onPopupClose);
        document.removeEventListener('alia:signup', this.onSignup);
        document.removeEventListener('alia:campaignMatched', this.onCampaignMatched);
        window.removeEventListener('pagehide', this.onPageHide);
        window.removeEventListener('pageshow', this.onPageShow);
    }
    /** Which of our stages an Alia event belongs to, or null for any other campaign. */
    stageOf(detail) {
        const id = campaignId(detail === null || detail === void 0 ? void 0 : detail.campaignID);
        if (id === null)
            return null;
        if (this.stage1Ids.includes(id))
            return 'stage1';
        // Stage 2 and the worm may share one campaign; stage 2 is checked first so
        // a shared campaign resumes the cascade rather than ending it.
        if (this.stage2Id !== null && id === this.stage2Id)
            return 'stage2';
        if (this.wormId !== null && id === this.wormId)
            return 'worm';
        return null;
    }
    handlePopupView(e) {
        var _a;
        const detail = e.detail || {};
        if (!this.ready)
            return void this.pending.push({ kind: 'view', detail });
        let stage = this.stageOf(detail);
        // Every Alia popup blocks us, including campaigns outside this cascade.
        this.revision++;
        // If this is the campaign we just asked to open, our own intent decides the
        // stage. Without this, opening the worm on a campaign shared with stage 2
        // immediately relabels it 'stage2' here, and the whole fix below unravels.
        const id = campaignId(detail === null || detail === void 0 ? void 0 : detail.campaignID);
        if (this.pendingOpen && id !== null && id === this.pendingOpen.id) {
            stage = this.pendingOpen.stage;
        }
        const requested = this.pendingOpen !== null && this.pendingOpen.id === id;
        if (((_a = this.pendingOpen) === null || _a === void 0 ? void 0 : _a.id) === id)
            this.pendingOpen = null;
        // Ordering guard. A follow-up on screen before stage 1 has been dismissed
        // this session, and not at our request, is a clash in the making: Alia
        // will show stage 1 the instant it closes. Whoever opened it — a trigger
        // someone re-added in Alia, a preview link, an `open` from another script —
        // hide it now and keep this session's turn for the proper moment.
        if (!requested && (stage === 'stage2' || stage === 'worm') && num(this.ssGet(SS.primaryClosedAt), 0) <= 0) {
            this.closeAlia();
            return;
        }
        this.openCampaignId = id;
        this.openPopupId = campaignId(detail.popupID);
        if (id !== null)
            this.announced.delete(id);
        this.ssSet(SS.closedAt, '');
        if (stage === 'stage1')
            this.ssSet(SS.primaryClosedAt, '');
        if (stage === 'stage2')
            this.ssSet(SS.stage2, '1');
        // Newest popup always wins: retract the worm if it's on screen.
        this.retract();
        this.clearTimers();
        this.openStage = stage;
        this.state = 'popup_open';
    }
    /**
     * Alia has matched a campaign on this page and may show it shortly. Alia also
     * announces the campaigns we open through the SDK, and — because it mounts a
     * live trigger-less campaign at page load so a later SDK open can trigger it —
     * it announces stage 2 and the worm campaign on every page they're live. None
     * of those is a popup to wait for. Events can land before the config fetch
     * resolves (stage ids unknown); they queue like the others and replay.
     */
    handleCampaignMatched(e) {
        var _a;
        const detail = e.detail || {};
        if (!this.ready)
            return void this.pending.push({ kind: 'matched', detail });
        const id = campaignId(detail.campaignID);
        if (id === null || ((_a = this.pendingOpen) === null || _a === void 0 ? void 0 : _a.id) === id)
            return;
        if (id === this.stage2Id || id === this.wormId)
            return;
        this.announced.set(id, Date.now());
    }
    /**
     * Milliseconds until it is our turn to open a popup, or 0 when it already is.
     * Alia's turn lasts while it has announced a popup it hasn't shown, and —
     * on a fresh page — until its SDK has initialised and had the chance to
     * announce one. Both end at ALIA_TURN_MS regardless.
     */
    aliaTurnMs() {
        const now = Date.now();
        let wait = 0;
        for (const [id, at] of this.announced) {
            const left = ALIA_TURN_MS - (now - at);
            if (left <= 0)
                this.announced.delete(id);
            else
                wait = Math.max(wait, left);
        }
        if (!this.aliaReady())
            wait = Math.max(wait, ALIA_TURN_MS - (now - this.bootAt));
        return Math.max(wait, 0);
    }
    /** Alia's SDK replaces the `window.alia` command queue with its own object once it has loaded. */
    aliaReady() {
        try {
            const a = window.alia;
            return !!a && !Array.isArray(a);
        }
        catch (_a) {
            return false;
        }
    }
    handlePopupClose(e) {
        var _a;
        const detail = e.detail || {};
        if (!this.ready)
            return void this.pending.push({ kind: 'close', detail });
        // The campaign id still decides whether the event is ours at all...
        const eventStage = this.stageOf(detail);
        if (this.converted() || this.state !== 'popup_open')
            return;
        if (campaignId(detail.campaignID) !== this.openCampaignId)
            return;
        const popupId = campaignId(detail.popupID);
        if (popupId !== null && this.openPopupId !== null && popupId !== this.openPopupId)
            return;
        this.revision++;
        // ...but which stage closed is answered by what we know is on screen, never
        // by the campaign id. Stage 2 and the worm are allowed to share a campaign,
        // and stageOf() breaks that tie in stage 2's favour so a shared campaign can
        // resume the cascade. Trusting it here classified every worm close as a
        // stage-2 close: the abandoned-popup cooldown was never written, so the
        // visitor was served the entire cascade again every session, indefinitely.
        const stage = (_a = this.openStage) !== null && _a !== void 0 ? _a : eventStage;
        this.ssSet(SS.closedAt, String(Date.now()));
        if (stage === 'stage1')
            this.ssSet(SS.primaryClosedAt, String(Date.now()));
        this.openStage = null;
        this.openCampaignId = null;
        this.openPopupId = null;
        // The worm's own popup closing is the end of the line — nothing follows it,
        // so record the abandoned-popup cooldown and stand down.
        if (stage === 'worm') {
            this.ckSet(CK.closed, '1', this.closeDays);
            this.state = 'suppressed';
            return;
        }
        this.state = 'gap';
        if (this.stage2Ready()) {
            this.armStage2(this.stage2WaitMs());
        }
        else {
            this.armGrace(this.boost(this.graceSeconds) * 1000);
        }
    }
    /**
     * Leaving the page with the worm's popup open never produces an alia:popupClose,
     * so the abandoned-popup cooldown would be lost and the cascade would start over
     * next session. The worm is the end of the line however the visitor leaves it.
     */
    handlePageHide() {
        this.revision++;
        this.clearTimers();
        if (this.openStage !== 'worm')
            return;
        if (this.converted())
            return;
        this.ckSet(CK.closed, '1', this.closeDays);
    }
    /**
     * A signup anywhere in Alia ends the cascade for a year — including campaigns
     * that aren't ours, since the visitor is now a subscriber either way. Only our
     * own stages are worth logging, and only for stage attribution.
     */
    handleSignup(e) {
        var _a;
        const detail = e.detail || {};
        if (!this.ready)
            return void this.pending.push({ kind: 'signup', detail });
        // Attribution has the same shared-campaign problem as handlePopupClose, and
        // it matters more here: reading the stage off the campaign id logged every
        // worm signup as alia_stage2, which is the one distinction worm_captures
        // exists to make. openStage is null for campaigns that aren't ours, so a
        // foreign signup still falls through to eventStage and stays unlogged.
        const eventStage = this.stageOf(detail);
        const stage = campaignId(detail.campaignID) === this.openCampaignId ? (_a = this.openStage) !== null && _a !== void 0 ? _a : eventStage : eventStage;
        this.revision++;
        this.ssSet(SS.converted, '1');
        this.ckSet(CK.subscribed, '1', this.subscribedDays);
        this.clearTimers();
        this.retract();
        this.openStage = null;
        this.state = 'converted';
        // Identity bridge — fires for ANY Alia signup, including campaigns that
        // aren't ours. Stage attribution cares which popup won; order attribution
        // only cares that this browser now has a name.
        void this.publishIdentity(detail.email);
        if (stage)
            this.logCapture(stage, detail.email);
    }
    /**
     * Tell the HeapsGood Ops web pixel who this visitor is, as a hash.
     *
     * The pixel runs in a sandboxed iframe: it cannot see this page's storage,
     * Alia's form, or this event. A Shopify custom analytics event is the only
     * supported channel from theme JS to a custom pixel — and the pixel side is
     * the only side holding the identifiers Ops attribution joins on (its own
     * session id and Shopify's clientId). Our sessionId() is a different
     * namespace and has never matched one: 0 of 2,166 captures in the 30 days to
     * 6 Sep 2026.
     *
     * A SHA-256 hex digest goes over the wire, never the address. The recipe is
     * shared with the pixel's checkout-time hash so the two resolve to the same
     * person — change it in one place and the bridge stops matching, silently:
     *   sha256('hg-identity-v1:' + email.trim().toLowerCase())
     *
     * Every failure path is a silent return. Alia has already captured the
     * address; attribution is never worth a broken signup.
     */
    publishIdentity(email) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const address = (email || '').trim().toLowerCase();
                if (!address)
                    return;
                // crypto.subtle needs a secure context. No hash, no publish — we never
                // fall back to sending the address itself.
                const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
                if (!subtle || typeof TextEncoder === 'undefined')
                    return;
                const digest = yield subtle.digest('SHA-256', new TextEncoder().encode(`hg-identity-v1:${address}`));
                const hash = Array.from(new Uint8Array(digest))
                    .map((b) => b.toString(16).padStart(2, '0'))
                    .join('');
                (_c = (_b = (_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.analytics) === null || _b === void 0 ? void 0 : _b.publish) === null || _c === void 0 ? void 0 : _c.call(_b, 'heapsgood_ops:identify', { identity_hash: hash });
            }
            catch (_d) {
                /* attribution only — never let it reach the signup path */
            }
        });
    }
    /** Exit intent skips the cart gate, but never sequencing, delays or consent gates. */
    handleExitIntent(e) {
        if (e.relatedTarget)
            return;
        if (e.clientY > 0)
            return;
        if (this.state !== 'observing' && this.state !== 'gap')
            return;
        if (this.stage2Ready())
            void this.tryOpenStage2('exit-intent');
        else
            void this.tryPeek('exit-intent');
    }
    /**
     * Fire-and-forget attribution log. Deliberately not awaited and never
     * surfaced: Alia has already captured the address, so a failed log is a
     * reporting gap, not a lost signup.
     */
    logCapture(stage, email) {
        if (!email)
            return;
        try {
            void fetch(this.captureUrl(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
                body: JSON.stringify({
                    stage: `alia_${stage}`,
                    email,
                    market: this.market,
                    path: window.location.pathname,
                    sessionId: this.sessionId(),
                }),
            }).catch(() => { });
        }
        catch (_a) {
            /* blocked — reporting only */
        }
    }
    // ---- resume across page loads (plain MPA — timers don't survive navigation) ----
    resumeTiming() {
        // Only a matching view -> close event pair is evidence of a dismissal.
        // Old popupSeen/closedAt flags and navigation with an open popup are not.
        const primaryClosedAt = num(this.ssGet(SS.primaryClosedAt), 0);
        if (primaryClosedAt <= 0) {
            this.state = 'observing';
            return;
        }
        this.state = 'gap';
        if (this.stage2Ready()) {
            this.armStage2(this.stage2WaitMs() - (Date.now() - primaryClosedAt));
        }
        else if (num(this.ssGet(SS.closedAt), 0) > 0) {
            this.armGrace(this.boost(this.graceSeconds) * 1000 - this.msSinceLastClose());
        }
    }
    // ---- timers ----
    boost(seconds) {
        return this.returnVisitBoost && this.isReturnVisit() ? seconds / 2 : seconds;
    }
    armGrace(remainingMs) {
        this.clearTimer('grace');
        this.graceTimer = window.setTimeout(() => {
            if (this.state === 'gap')
                void this.tryPeek();
        }, Math.max(remainingMs, MIN_SETTLE_MS));
    }
    armStage2(remainingMs) {
        this.clearTimer('stage2');
        this.stage2Timer = window.setTimeout(() => {
            if (this.state === 'gap' || this.state === 'observing')
                void this.tryOpenStage2();
        }, Math.max(remainingMs, MIN_SETTLE_MS));
    }
    /** Retry an earned stage while waiting for engagement, preserving its turn. */
    armEngagementRecheck() {
        if (this.engagementTimer !== null)
            return;
        this.engagementTimer = window.setInterval(() => {
            if (this.state !== 'observing' && this.state !== 'gap') {
                this.clearTimer('engagement');
                return;
            }
            if (this.stage2Ready())
                void this.tryOpenStage2();
            else
                void this.tryPeek();
        }, 3000);
    }
    clearTimer(which) {
        const map = {
            grace: 'graceTimer',
            engagement: 'engagementTimer',
            stage2: 'stage2Timer',
        };
        const key = map[which];
        const id = this[key];
        if (id === null)
            return;
        if (which === 'engagement')
            clearInterval(id);
        else
            clearTimeout(id);
        this[key] = null;
    }
    clearTimers() {
        ['grace', 'engagement', 'stage2'].forEach((t) => this.clearTimer(t));
    }
    // ---- stage 2 ----
    /** How long since a popup last closed. Infinity when none has this session. */
    msSinceLastClose() {
        const raw = this.ssGet(SS.closedAt);
        if (!raw)
            return Number.POSITIVE_INFINITY;
        const at = num(raw, 0);
        return at > 0 ? Date.now() - at : Number.POSITIVE_INFINITY;
    }
    stage2Ready() {
        return this.stage2Id !== null && this.ssGet(SS.stage2) !== '1' && num(this.ssGet(SS.primaryClosedAt), 0) > 0;
    }
    /** Every entry point shares the same dismissal, quiet-period and eligibility gates. */
    tryOpenStage2(trigger = 'timer') {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== 'gap' || !this.stage2Ready())
                return false;
            const remaining = this.stage2RemainingMs();
            if (remaining > 0) {
                this.armStage2(remaining);
                return false;
            }
            // Alia's turn: a popup it has announced (or may be about to) must show and
            // close first. Re-arm rather than consume — the close, if it comes, starts
            // a fresh delay; if it never comes, the window passes and we proceed.
            const aliaTurn = this.aliaTurnMs();
            if (aliaTurn > 0) {
                this.armStage2(aliaTurn);
                return false;
            }
            if (!this.eligible()) {
                this.clearTimer('engagement');
                return false;
            }
            if (!this.engagementMet()) {
                this.armEngagementRecheck();
                return false;
            }
            const revision = this.revision;
            const hasItems = trigger !== 'exit-intent' && this.suppressWhenCartNonempty && (yield this.cartHasItems());
            if (revision !== this.revision || !this.eligible())
                return false;
            if (hasItems) {
                // Re-check state after the await: a popupView or signup can land during
                // the /cart.js fetch, and blindly writing 'gap' over 'popup_open' would
                // make the eventual popupClose early-return — stranding openStage and
                // losing the session's choreography.
                if (this.state !== 'gap')
                    return false;
                // Stop automatic attempts without consuming the turn: a later explicit
                // exit intent may still show it. Do not poll the cart or skip to the worm.
                this.clearTimers();
                return false;
            }
            return this.openStage2();
        });
    }
    stage2WaitMs() {
        return Math.max(MIN_GAP_AFTER_CLOSE_MS, this.stage2DelaySeconds * 1000);
    }
    stage2RemainingMs() {
        return Math.max(this.stage2WaitMs() - (Date.now() - num(this.ssGet(SS.primaryClosedAt), Date.now())), this.stage2WaitMs() - this.msSinceLastClose());
    }
    openStage2() {
        if (this.state !== 'gap' || !this.stage2Ready() || !this.eligible())
            return false;
        if (this.stage2RemainingMs() > 0) {
            this.armStage2(this.stage2RemainingMs());
            return false;
        }
        if (this.aliaTurnMs() > 0) {
            this.armStage2(this.aliaTurnMs());
            return false;
        }
        this.ssSet(SS.stage2, '1');
        this.ssSet(SS.closedAt, '');
        this.clearTimers();
        this.state = 'opening';
        this.revision++;
        // A timeout cannot cancel an SDK command already queued inside Alia. Keep
        // the attempt reserved: advancing to the worm would race a delayed view.
        this.openAlia(this.stage2Id, { ignoreTargeting: false, stage: 'stage2' });
        return true;
    }
    /**
     * Push an open onto Alia's queue. Safe before the SDK loads, and if it never
     * does. `ignoreTargeting` is passed explicitly every time — Alia's documented
     * behaviour when it's omitted is different again (targeting evaluated from
     * page-load state), which is not something we want to inherit by accident.
     */
    openAlia(id, opts) {
        // Record what we asked for, before asking. When stage 2 and the worm share a
        // campaign the popupView that follows cannot be classified by id alone, and
        // stageOf() would answer 'stage2' for both. Setting this inside openAlia
        // rather than at the call sites means a new open path can't forget to.
        this.pendingOpen = { stage: opts.stage, id };
        try {
            const w = window;
            w.alia = w.alia || [];
            w.alia.push({ type: 'open', campaignID: id, ignoreTargeting: opts.ignoreTargeting });
        }
        catch (_a) {
            /* No confirmed view: stay reserved, never race a queued SDK open. */
        }
    }
    /** Hide whatever Alia has on screen. Alia answers with its own popupClose, which handlePopupClose ignores unless the popup was ours to track. */
    closeAlia() {
        try {
            const w = window;
            w.alia = w.alia || [];
            w.alia.push({ type: 'close' });
        }
        catch (_a) {
            /* nothing to do — the popup stays, as before this guard existed */
        }
    }
    // ---- worm teaser ----
    wormAllowed() {
        if (this.wormId === null)
            return false;
        return !this.wormDesktopOnly || this.isDesktop();
    }
    /** Every gate that must still hold at the moment a stage actually fires. */
    eligible() {
        if (this.converted())
            return false;
        if (this.ckGet(CK.subscribed) || this.ckGet(CK.dismissed) || this.ckGet(CK.closed))
            return false;
        return true;
    }
    tryPeek(trigger = 'timer') {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== 'gap' || this.pendingOpen || num(this.ssGet(SS.closedAt), 0) <= 0)
                return;
            if (num(this.ssGet(SS.primaryClosedAt), 0) <= 0 || this.stage2Ready())
                return;
            const remaining = this.boost(this.graceSeconds) * 1000 - this.msSinceLastClose();
            if (remaining > 0) {
                this.armGrace(remaining);
                return;
            }
            const aliaTurn = this.aliaTurnMs();
            if (aliaTurn > 0) {
                this.armGrace(aliaTurn);
                return;
            }
            if (!this.eligible() || this.ssGet(SS.done) === '1') {
                this.clearTimer('engagement');
                return;
            }
            if (!this.wormAllowed()) {
                this.clearTimers();
                this.state = 'suppressed';
                return;
            }
            if (!this.engagementMet()) {
                this.armEngagementRecheck();
                return;
            }
            const revision = this.revision;
            const hasItems = trigger !== 'exit-intent' && this.suppressWhenCartNonempty && (yield this.cartHasItems());
            if (revision !== this.revision || this.state !== 'gap' || !this.eligible() || this.pendingOpen)
                return;
            if (hasItems) {
                // Keep the earned turn available to a later exit intent, without polling.
                this.clearTimers();
                return;
            }
            this.state = 'peeking';
            this.ssSet(SS.done, '1'); // one peek per session
            this.clearTimers();
            this.classList.add('is-peeking');
        });
    }
    /**
     * Worm clicked. `ignoreTargeting: true` here and nowhere else: this is an
     * explicit request from the visitor, and a campaign that refuses to open
     * would leave the click doing visibly nothing.
     */
    openWormCampaign() {
        if (this.state !== 'peeking' || this.wormId === null)
            return;
        this.clearTimers();
        this.retract();
        this.state = 'opening';
        this.revision++;
        this.openAlia(this.wormId, { ignoreTargeting: true, stage: 'worm' });
    }
    retract() {
        this.classList.remove('is-peeking');
    }
    dismiss() {
        this.ckSet(CK.dismissed, '1', this.dismissDays);
        this.state = 'dismissed';
        this.clearTimers();
        this.retract();
    }
    // ---- storage (all reads/writes go through these; failures are swallowed) ----
    converted() {
        return this.ssGet(SS.converted) === '1';
    }
    ssGet(key) {
        var _a, _b;
        try {
            return (_a = this.sessionMemory.get(key)) !== null && _a !== void 0 ? _a : sessionStorage.getItem(key);
        }
        catch (_c) {
            return (_b = this.sessionMemory.get(key)) !== null && _b !== void 0 ? _b : null;
        }
    }
    ssSet(key, value) {
        try {
            sessionStorage.setItem(key, value);
            this.sessionMemory.delete(key);
        }
        catch (_a) {
            // Use memory only for failed writes, so a BFCache-restored page still
            // reads newer dismissal/conversion state written by subsequent pages.
            this.sessionMemory.set(key, value);
        }
    }
    ckGet(name) {
        try {
            return Cookies.get(name);
        }
        catch (_a) {
            return undefined;
        }
    }
    ckSet(name, value, expires) {
        try {
            Cookies.set(name, value, { path: '/', expires });
        }
        catch (_a) {
            /* blocked — ignore */
        }
    }
    // ---- session / gates ----
    ensureSessionId() {
        if (!this.ssGet(SS.sessionId)) {
            const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `sess-${Date.now()}-${Math.random().toString(16).slice(2)}`;
            this.ssSet(SS.sessionId, id);
        }
    }
    sessionId() {
        return this.ssGet(SS.sessionId) || '';
    }
    trackSession() {
        if (!this.ssGet(SS.t0))
            this.ssSet(SS.t0, String(Date.now()));
        this.ssSet(SS.pages, String(num(this.ssGet(SS.pages), 0) + 1));
        if (this.ssGet(SS.sessionCounted) !== '1') {
            this.ckSet(CK.visits, String(num(this.ckGet(CK.visits), 0) + 1), 30);
            this.ssSet(SS.sessionCounted, '1');
        }
    }
    isReturnVisit() {
        return num(this.ckGet(CK.visits), 1) >= 2;
    }
    secondsOnSite() {
        return (Date.now() - num(this.ssGet(SS.t0), Date.now())) / 1000;
    }
    /** Engagement gate: min pages OR min seconds. */
    engagementMet() {
        return num(this.ssGet(SS.pages), 1) >= this.minPages || this.secondsOnSite() >= this.minSeconds;
    }
    isDesktop() {
        return window.matchMedia('(min-width: 750px)').matches;
    }
    cartHasItems() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const res = yield fetch('/cart.js', { headers: { Accept: 'application/json' } });
                if (!res.ok)
                    return true;
                const cart = yield res.json();
                return typeof cart.item_count !== 'number' || cart.item_count > 0;
            }
            catch (_a) {
                return true;
            }
        });
    }
}
function num(v, fallback) {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}
/** Plain-object guard — arrays and primitives become null. */
function obj(raw) {
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
}
/**
 * Alia campaign ids are integers. Numeric strings are accepted because the
 * config round-trips through JSON and an event payload may stringify it, but
 * anything that isn't a positive whole number becomes null — pushing a NaN
 * campaignID at Alia fails silently, which is the worst way to fail.
 */
function campaignId(v) {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
}
if (!customElements.get('hg-popup-cascade')) {
    customElements.define('hg-popup-cascade', PopupCascade);
}
export {};
